// Shared number/money formatter. Every monetary value in the app renders through
// these helpers (see product docs 01 "Global UX Standards", section 1).
//
// Rules:
//   Fiat (USD):   2 decimals, thousands separators        -> 76,430.29   (callers prepend "$")
//   Tokens:       trimmed decimals + thousands separators  -> 294.98      (callers append ticker)
//   Counts:       thousands separators                     -> 1,412
//   Compact:      tight-space / chart axes                 -> $30.6M
//   Hash/wallet:  truncate middle                          -> 0x2256...c7b
//
// formatUSD returns the number WITHOUT a "$" symbol and formatCrypto returns the
// number WITHOUT a ticker, because existing call sites add those themselves.

// Insert thousands separators into the integer part of an already-fixed string.
// Handles a leading "-" sign and an optional decimal part.
const groupThousands = (numericStr) => {
  const sign = numericStr.startsWith('-') ? '-' : '';
  const unsigned = sign ? numericStr.slice(1) : numericStr;
  const [intPart, decPart] = unsigned.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${sign}${grouped}.${decPart}` : `${sign}${grouped}`;
};

export const formatCrypto = (amount, maxDecimals = 8) => {
  if (amount === null || amount === undefined || amount === '') return '0';

  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '0';
  if (num === 0) return '0';

  let str = num.toFixed(Math.min(maxDecimals, 20));

  // Trim trailing zeros (and a dangling decimal point) so tokens read cleanly.
  if (str.includes('.')) {
    str = str.replace(/\.?0+$/, '');
  }

  return groupThousands(str);
};

export const formatUSD = (amount, maxDecimals = 2) => {
  if (amount === null || amount === undefined || amount === '') return '0.00';

  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '0.00';

  // Fiat is always shown with a fixed number of decimals (default 2) and
  // thousands separators. No more $76430.2853637.
  const decimals = Number.isFinite(maxDecimals) ? maxDecimals : 2;
  return groupThousands(num.toFixed(decimals));
};

export const formatWithCurrency = (amount, currency = 'USD', options = {}) => {
  const { showSymbol = true, maxDecimals } = options;

  const isUSD = currency?.toUpperCase() === 'USD';
  const formatted = isUSD
    ? formatUSD(amount, maxDecimals ?? 2)
    : formatCrypto(amount, maxDecimals ?? 8);

  if (!showSymbol) return formatted;

  if (isUSD) {
    return `$${formatted}`;
  }

  return `${formatted} ${currency}`;
};

export const formatTokenAmount = (amount, decimals = 8) => {
  if (amount === null || amount === undefined || amount === '') return '0';

  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '0';
  if (num === 0) return '0';

  let str = num.toFixed(Math.min(decimals, 20));

  if (str.includes('.')) {
    str = str.replace(/\.?0+$/, '');
  }

  return groupThousands(str);
};

// Whole-number counts with thousands separators (e.g. "1,412").
export const formatCount = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Math.round(num).toLocaleString('en-US');
};

// Elapsed time as a compact two-unit label, for KPI cards and table cells:
//   45s   ·   12m   ·   2h 39m   ·   3d 4h
// Returns null for a missing/invalid value so callers can render their own
// "no data" state rather than showing a misleading "0s".
export const formatDuration = (seconds) => {
  const total =
    typeof seconds === 'string' ? parseFloat(seconds) : seconds;

  if (total === null || total === undefined || isNaN(total) || total < 0) {
    return null;
  }

  const rounded = Math.round(total);
  if (rounded < 60) return `${rounded}s`;

  const minutes = Math.floor(rounded / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remMinutes = minutes % 60;
    return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
};

// Truncate a wallet address / tx hash to "0x2256...c7b" for tight spaces.
export const truncateHash = (value, lead = 6, tail = 4) => {
  if (!value || typeof value !== 'string') return '';
  if (value.length <= lead + tail + 1) return value;
  return `${value.slice(0, lead)}...${value.slice(-tail)}`;
};

export const parseAmount = (amount) => {
  if (typeof amount === 'number') return amount;
  if (!amount || amount === '') return 0;
  return parseFloat(amount) || 0;
};

export const amountsEqual = (a, b, tolerance = 1e-18) => {
  const numA = parseAmount(a);
  const numB = parseAmount(b);
  return Math.abs(numA - numB) < tolerance;
};

export const formatCompact = (amount, currency = 'USD') => {
  const num = parseAmount(amount);
  if (num === 0) return currency === 'USD' ? '$0.00' : `0 ${currency}`;

  const isUSD = currency?.toUpperCase() === 'USD';

  if (Math.abs(num) >= 1000000000) {
    const formatted = (num / 1000000000).toFixed(2);
    return isUSD ? `$${formatted}B` : `${formatted}B ${currency}`;
  }
  if (Math.abs(num) >= 1000000) {
    const formatted = (num / 1000000).toFixed(2);
    return isUSD ? `$${formatted}M` : `${formatted}M ${currency}`;
  }
  if (Math.abs(num) >= 10000) {
    const formatted = (num / 1000).toFixed(2);
    return isUSD ? `$${formatted}K` : `${formatted}K ${currency}`;
  }

  return formatWithCurrency(amount, currency);
};

export default {
  formatCrypto,
  formatUSD,
  formatWithCurrency,
  formatTokenAmount,
  formatCount,
  formatDuration,
  truncateHash,
  parseAmount,
  amountsEqual,
  formatCompact,
};
